import { useInfiniteQuery } from "@tanstack/react-query";

import { TOOL_REGISTRY_VERSIONS_KEY } from "./cache-utils";
import type { ToolVersionRecord } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import type { PaginatedResponse } from "@/queries/types";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export type ListToolVersionsResponse = PaginatedResponse<ToolVersionRecord[], "versions", string>;

export const useListToolVersions = (workspaceId: number | null, toolId: number | null) => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const perPage = 30;

  return useInfiniteQuery({
    queryKey: [TOOL_REGISTRY_VERSIONS_KEY, workspaceId, toolId, perPage],
    queryFn: async ({ pageParam = 1 }): Promise<ListToolVersionsResponse> => {
      if (!workspaceId || !toolId) {
        return {
          success: true,
          versions: [],
          has_prev: false,
          has_next: false,
          prev_num: null,
          next_num: null,
          page: 1,
          pages: 0,
          per_page: perPage,
          total: 0,
        };
      }
      const url = new URL(ENDPOINTS.tool_registry.versions(workspaceId, toolId));
      url.searchParams.set("page", pageParam.toString());
      url.searchParams.set("per_page", perPage.toString());

      const res = await fetch(url.toString(), {
        headers: jsonAuthHeaders(userToken),
      });
      return handleResponse(res);
    },
    getNextPageParam: lastPage => (lastPage.has_next ? lastPage.next_num : undefined),
    enabled: !!workspaceId && !!toolId && !!userToken,
  });
};
